import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

/**
 * New Relic Browser Agent Service
 * Handles initialization and provides helper methods for custom tracking
 */
class NewRelicService {
  private agent: BrowserAgent | null = null;
  private isInitialized = false;
  private currentUserId: string | number | null = null;

  /**
   * Initialize New Relic Browser Agent
   * Only initializes if REACT_APP_NEW_RELIC_ENABLED is 'true'
   */
  init() {
    // Check if New Relic is enabled via environment variable
    const isEnabled = process.env.REACT_APP_NEW_RELIC_ENABLED === 'true';

    if (!isEnabled) {
      return;
    }

    // Get configuration from environment variables
    const accountId = process.env.REACT_APP_NEW_RELIC_ACCOUNT_ID;
    const applicationId = process.env.REACT_APP_NEW_RELIC_APPLICATION_ID;
    const licenseKey = process.env.REACT_APP_NEW_RELIC_LICENSE_KEY;
    const trustKey = process.env.REACT_APP_NEW_RELIC_TRUST_KEY;
    const agentId = process.env.REACT_APP_NEW_RELIC_AGENT_ID;

    // Validate required configuration
    if (!accountId || !applicationId || !licenseKey || !trustKey || !agentId) {
      console.warn('New Relic: Missing required configuration. Skipping initialization.');
      console.warn('Required env vars: REACT_APP_NEW_RELIC_ACCOUNT_ID, REACT_APP_NEW_RELIC_APPLICATION_ID, REACT_APP_NEW_RELIC_LICENSE_KEY, REACT_APP_NEW_RELIC_TRUST_KEY, REACT_APP_NEW_RELIC_AGENT_ID');
      return;
    }

    try {
      const options = {
        info: {
          applicationID: applicationId,
          beacon: 'bam.nr-data.net',
          errorBeacon: 'bam.nr-data.net',
          licenseKey: licenseKey,
          sa: 1
        },
        init: {
          ajax: {
            deny_list: ['bam.nr-data.net'],
            // Reduce AJAX harvesting frequency to minimize requests
            harvestTimeSeconds: 60
          },
          browser_consent_mode: {
            enabled: false
          },
          distributed_tracing: {
            enabled: false  // Disable if not needed for performance
          },
          page_action: {
            enabled: true  // Keep custom events enabled
          },
          page_view_event: {
            enabled: true
          },
          page_view_timing: {
            enabled: true
          },
          session_trace: {
            enabled: false  // Disable session traces to reduce data
          },
          session_replay: {
            enabled: false,  // Disable session replay - major data saver
            harvestTimeSeconds: 60,
            sampling_rate: 0,
            error_sampling_rate: 0
          },
          jserrors: {
            enabled: true,  // Keep error tracking
            harvestTimeSeconds: 10
          },
          metrics: {
            enabled: false  // Disable detailed metrics if not needed
          },
          spa: {
            enabled: false  // Disable SPA monitoring if not critical
          },
          performance: {
            capture_detail: false,
            capture_marks: false,
            capture_measures: false  // Changed to false to reduce data
          },
          privacy: {
            cookies_enabled: true
          }
        },
        loader_config: {
          accountID: parseInt(accountId, 10),
          agentID: parseInt(agentId, 10),
          applicationID: parseInt(applicationId, 10),
          licenseKey: licenseKey,
          trustKey: parseInt(trustKey, 10)
        }
      };

      // The agent loader code executes immediately on instantiation
      this.agent = new BrowserAgent(options);
      this.isInitialized = true;
    } catch (error) {
      console.error('New Relic: Initialization failed', error);
    }
  }

  /**
   * Check if New Relic is initialized and available
   */
  isAvailable(): boolean {
    return this.isInitialized && !!window.newrelic;
  }

  /**
   * Set a custom attribute for the current page view
   * @param name - The attribute name
   * @param value - The attribute value
   */
  setCustomAttribute(name: string, value: string | number | boolean) {
    if (this.isAvailable() && window.newrelic) {
      try {
        window.newrelic.setCustomAttribute(name, value);
      } catch (error) {
        console.error('New Relic: Error setting custom attribute', error);
      }
    }
  }

  /**
   * Track a custom page action (user interaction)
   * NOTE: User/org context should be set once via setUserContext() after login
   * rather than sending with every event to reduce payload size
   * @param name - The action name (e.g., 'ReviewSearch', 'LoadCreated')
   * @param attributes - Optional custom attributes
   */
  addPageAction(name: string, attributes?: Record<string, any>) {
    if (this.isAvailable() && window.newrelic) {
      try {
        window.newrelic.addPageAction(name, attributes);
      } catch (error) {
        console.error('New Relic: Error adding page action', error);
      }
    }
  }

  /**
   * Report a custom error
   * @param error - The error object
   * @param customAttributes - Optional custom attributes
   */
  noticeError(error: Error, customAttributes?: Record<string, any>) {
    if (this.isAvailable() && window.newrelic) {
      try {
        window.newrelic.noticeError(error, customAttributes);
      } catch (error) {
        console.error('New Relic: Error reporting error', error);
      }
    }
  }

  /**
   * Set user context information
   * Call this after user logs in to associate actions with specific users
   * Only sets context once per user session to avoid duplicate calls
   * @param userId - The user ID
   * @param username - The username
   * @param orgId - The organization ID
   * @param orgName - The organization name
   */
  setUserContext(userId: string | number, username: string, orgId?: string | number, orgName?: string) {
    if (!this.isAvailable()) {
      return;
    }

    // Skip if user context is already set for this user
    if (this.currentUserId === userId) {
      return;
    }

    // Set user context attributes
    this.setCustomAttribute('userId', userId.toString());
    this.setCustomAttribute('username', username);

    if (orgId) {
      this.setCustomAttribute('orgId', orgId.toString());
    }

    if (orgName) {
      this.setCustomAttribute('orgName', orgName);
    }

    // Remember current user to prevent duplicate calls
    this.currentUserId = userId;
  }

  /**
   * Clear user context information
   * Call this when user logs out to allow setting context for a new user
   */
  clearUserContext() {
    this.currentUserId = null;
  }

  /**
   * Set page view name
   * Useful for SPAs to track different views
   * @param name - The page/view name
   */
  setPageViewName(name: string) {
    if (this.isAvailable() && window.newrelic) {
      try {
        window.newrelic.setPageViewName(name);
      } catch (error) {
        console.error('New Relic: Error setting page view name', error);
      }
    }
  }
}

export default new NewRelicService();
